import { useEffect, useMemo, useRef, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierProviderAddOnService,
  type UpdateCarrierAddOnServiceInput,
  useUpdateCarrierAddOnService,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { hasHttpStatus } from "@/lib/api-error";

export function PickupServiceOverview({
  service,
  onDirtyChange,
}: {
  service: CarrierProviderAddOnService;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const update = useUpdateCarrierAddOnService();
  const initial = useMemo(() => overviewValues(service), [service]);
  const [baseline, setBaseline] = useState(initial);
  const [name, setName] = useState(initial.name);
  const [active, setActive] = useState(initial.active);
  const [dimFactorCm, setDimFactorCm] = useState(initial.dimFactorCm);
  const [dimFactorInch, setDimFactorInch] = useState(initial.dimFactorInch);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submissionLock = useRef(false);
  const pending = submitting || update.isPending;
  const dirty =
    name !== baseline.name ||
    active !== baseline.active ||
    dimFactorCm !== baseline.dimFactorCm ||
    dimFactorInch !== baseline.dimFactorInch;

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(
    () => () => {
      onDirtyChange(false);
    },
    [onDirtyChange]
  );

  const handleSave = async () => {
    if (submissionLock.current) return;
    setError(null);
    const trimmedName = name.trim();
    const cm = parsePositiveNumber(dimFactorCm);
    const inch = parsePositiveNumber(dimFactorInch);
    if (!trimmedName) {
      setError("Enter a pickup service name.");
      return;
    }
    if (cm === undefined || inch === undefined) {
      setError("DIM factors must be positive numbers or blank.");
      return;
    }

    const patch: UpdateCarrierAddOnServiceInput = {};
    if (trimmedName !== baseline.name) patch.name = trimmedName;
    if (active !== baseline.active) patch.active = active;
    if (cm !== parsePositiveNumber(baseline.dimFactorCm)) {
      patch.dimFactorCm = cm;
    }
    if (inch !== parsePositiveNumber(baseline.dimFactorInch)) {
      patch.dimFactorInch = inch;
    }
    if (Object.keys(patch).length === 0) return;

    submissionLock.current = true;
    setSubmitting(true);
    try {
      await update.mutateAsync({ id: service.id, patch });
      toast.success("Pickup service updated.");
      setName(trimmedName);
      setBaseline({
        name: trimmedName,
        active,
        dimFactorCm,
        dimFactorInch,
      });
    } catch (caught) {
      const response = caught as { response?: { data?: { error?: string } } };
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to update this pickup service."
        : (response.response?.data?.error ??
          "Pickup service could not be updated.");
      setError(message);
      toast.error(message);
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service configuration</CardTitle>
        <p className="text-sm text-muted-foreground">
          Code and provider are permanent. Deactivation prevents new bookings
          while preserving existing references.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-[max-content_1fr] gap-x-5 gap-y-2 rounded-md border bg-muted/30 p-3 text-sm">
          <dt className="text-muted-foreground">Code</dt>
          <dd className="break-all font-mono">{service.code}</dd>
          <dt className="text-muted-foreground">Provider</dt>
          <dd>{service.provider.name}</dd>
        </dl>
        <div className="grid gap-4 md:grid-cols-2">
          <label
            htmlFor="pickup-overview-name"
            className="space-y-1 text-sm font-medium md:col-span-2"
          >
            <span>Name</span>
            <Input
              id="pickup-overview-name"
              aria-label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={pending}
            />
          </label>
          <label
            htmlFor="pickup-overview-dim-cm"
            className="space-y-1 text-sm font-medium"
          >
            <span>DIM factor (cm³)</span>
            <Input
              id="pickup-overview-dim-cm"
              aria-label="DIM factor (cm³)"
              inputMode="decimal"
              value={dimFactorCm}
              onChange={(event) => setDimFactorCm(event.target.value)}
              placeholder="Not set"
              disabled={pending}
            />
          </label>
          <label
            htmlFor="pickup-overview-dim-inch"
            className="space-y-1 text-sm font-medium"
          >
            <span>DIM factor (in³)</span>
            <Input
              id="pickup-overview-dim-inch"
              aria-label="DIM factor (in³)"
              inputMode="decimal"
              value={dimFactorInch}
              onChange={(event) => setDimFactorInch(event.target.value)}
              placeholder="Not set"
              disabled={pending}
            />
          </label>
        </div>
        <label
          htmlFor="pickup-overview-active"
          className="flex min-h-11 items-center gap-3 rounded-md border p-3 text-sm"
        >
          <Checkbox
            id="pickup-overview-active"
            checked={active}
            onCheckedChange={(checked) => setActive(checked === true)}
            disabled={pending}
          />
          <span>
            <span className="block font-medium">
              Available for new bookings
            </span>
            <span className="block text-xs text-muted-foreground">
              Existing bookings and references remain unchanged.
            </span>
          </span>
        </label>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "All changes saved"}
          </span>
          <Button
            onClick={() => void handleSave()}
            disabled={pending || !dirty}
            size="touch"
            className="w-full sm:w-auto"
          >
            <Save className="mr-2 h-4 w-4" />
            {pending ? "Saving…" : "Save overview"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function overviewValues(service: CarrierProviderAddOnService) {
  return {
    name: service.name,
    active: service.active,
    dimFactorCm:
      service.pickupDetails?.dimFactorCm == null
        ? ""
        : String(service.pickupDetails.dimFactorCm),
    dimFactorInch:
      service.pickupDetails?.dimFactorInch == null
        ? ""
        : String(service.pickupDetails.dimFactorInch),
  };
}

function parsePositiveNumber(value: string): number | null | undefined {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}
